// "Custodito H24" trust plate — self-contained (all CSS scoped under .gsm-plate).
// Ported from the Elementor HTML/CSS snippet; rendered once in the hero.
const CSS = `
  .gsm-plate {
    --gsm-gold: #f5b301;
    --gsm-gold-glow: #ffd34d;
    --gsm-ink: #f5f1e6;

    position: relative;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
    padding: 21px 36px;
    max-width: 100%;
    box-sizing: border-box;
    background: radial-gradient(ellipse at top, #1c2a40 0%, #0b1322 70%);
    border-radius: 4px;
    border: 1px solid rgba(245,179,1,0.35);
    box-shadow:
      0 0 0 1px rgba(255,255,255,0.04) inset,
      0 30px 60px -25px rgba(0,0,0,0.55),
      0 0 40px -15px rgba(245,179,1,0.25);
    overflow: hidden;
    font-family: "Inter", "Helvetica Neue", Arial, sans-serif;
  }

  /* Gold corner brackets */
  .gsm-plate::before,
  .gsm-plate::after {
    content: "";
    position: absolute;
    width: 18px;
    height: 18px;
    border: 2px solid var(--gsm-gold);
    pointer-events: none;
  }
  .gsm-plate::before {
    top: 8px; left: 8px;
    border-right: none;
    border-bottom: none;
  }
  .gsm-plate::after {
    bottom: 8px; right: 8px;
    border-left: none;
    border-top: none;
  }

  /* Animated gold scan line */
  .gsm-plate .gsm-scan {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
  }
  .gsm-plate .gsm-scan::before {
    content: "";
    position: absolute;
    left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--gsm-gold), transparent);
    opacity: 0.7;
    animation: gsmScanline 3.5s linear infinite;
  }
  @keyframes gsmScanline {
    0%   { top: -2%; opacity: 0; }
    10%  { opacity: 0.8; }
    90%  { opacity: 0.8; }
    100% { top: 102%; opacity: 0; }
  }

  /* Title */
  .gsm-plate .gsm-title {
    position: relative;
    z-index: 1;
    font-weight: 900;
    font-size: 17px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--gsm-ink);
    display: inline-flex;
    align-items: center;
    gap: 14px;
    margin: 0;
  }
  .gsm-plate .gsm-bar {
    width: 24px;
    height: 2px;
    background: var(--gsm-gold);
    box-shadow: 0 0 10px var(--gsm-gold);
    animation: gsmBarGrow 3s ease-in-out infinite;
    transform-origin: center;
  }
  @keyframes gsmBarGrow {
    0%, 100% { transform: scaleX(1); opacity: 0.6; }
    50%      { transform: scaleX(1.6); opacity: 1; }
  }
  .gsm-plate .gsm-glow {
    background: linear-gradient(
      90deg,
      var(--gsm-ink) 0%,
      var(--gsm-ink) 40%,
      var(--gsm-gold-glow) 50%,
      var(--gsm-ink) 60%,
      var(--gsm-ink) 100%
    );
    background-size: 250% 100%;
    -webkit-background-clip: text;
            background-clip: text;
    color: transparent;
    animation: gsmGoldSweep 4s ease-in-out infinite;
  }
  @keyframes gsmGoldSweep {
    0%, 100% { background-position: 150% 0; }
    50%      { background-position: -50% 0; }
  }

  /* Stars — spin + flash */
  .gsm-plate .gsm-stars {
    display: inline-flex;
    gap: 10px;
    z-index: 1;
  }
  .gsm-plate .gsm-stars svg {
    width: 22px;
    height: 22px;
    fill: var(--gsm-gold);
    filter: drop-shadow(0 0 6px rgba(245,179,1,0.5));
    transform-origin: center;
    animation: gsmSpinFlash 4s ease-in-out infinite;
  }
  .gsm-plate .gsm-stars svg:nth-child(1) { animation-delay: 0.00s; }
  .gsm-plate .gsm-stars svg:nth-child(2) { animation-delay: 0.18s; }
  .gsm-plate .gsm-stars svg:nth-child(3) { animation-delay: 0.36s; }
  .gsm-plate .gsm-stars svg:nth-child(4) { animation-delay: 0.54s; }
  .gsm-plate .gsm-stars svg:nth-child(5) { animation-delay: 0.72s; }

  @keyframes gsmSpinFlash {
    0%, 55%, 100% {
      transform: rotate(0) scale(1);
      fill: var(--gsm-gold);
      filter: drop-shadow(0 0 6px rgba(245,179,1,0.5));
    }
    65% {
      transform: rotate(180deg) scale(1.3);
      fill: #fff7d6;
      filter: drop-shadow(0 0 14px rgba(255,247,214,0.95));
    }
    75% {
      transform: rotate(360deg) scale(1);
      fill: var(--gsm-gold);
    }
  }

  /* Footer micro-text */
  .gsm-plate .gsm-tag {
    z-index: 1;
    font-size: 10px;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: rgba(245,241,230,0.55);
    border-top: 1px solid rgba(245,179,1,0.25);
    padding-top: 10px;
    width: 100%;
    text-align: center;
  }

  @media (max-width: 600px) {
    .gsm-plate { padding: 16px 44px; gap: 12px; }
    .gsm-plate .gsm-title { font-size: 13px; letter-spacing: 0.18em; gap: 8px; }
    .gsm-plate .gsm-bar { width: 16px; }
    .gsm-plate .gsm-stars { gap: 6px; }
    .gsm-plate .gsm-stars svg { width: 18px; height: 18px; }
    .gsm-plate .gsm-tag { font-size: 7px; letter-spacing: 0.16em; }
  }

  @media (prefers-reduced-motion: reduce) {
    .gsm-plate .gsm-scan::before,
    .gsm-plate .gsm-bar,
    .gsm-plate .gsm-glow,
    .gsm-plate .gsm-stars svg { animation: none; }
  }
`

const STAR = 'M12 .587l3.668 7.431L24 9.75l-6 5.847 1.42 8.28L12 19.771l-7.42 4.106L6 15.597 0 9.75l8.332-1.732z'

export function TrustPlate() {
  return (
    <>
      <style>{CSS}</style>
      <div className="gsm-plate" role="group" aria-label="Custodito H24, valutazione 5 stelle su 5">
        <span className="gsm-scan" aria-hidden="true" />

        <div className="gsm-title">
          <span className="gsm-bar" aria-hidden="true" />
          <span className="gsm-glow">Custodito H24</span>
          <span className="gsm-bar" aria-hidden="true" />
        </div>

        <div className="gsm-stars" aria-label="5 stelle su 5">
          {Array.from({ length: 5 }).map((_, i) => (
            <svg key={i} viewBox="0 0 24 24" aria-hidden="true">
              <path d={STAR} />
            </svg>
          ))}
        </div>

        <div className="gsm-tag">Sicurezza · Sorveglianza · 24 / 7</div>
      </div>
    </>
  )
}
